import {
  startOfDay,
  startOfQuarter,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
  endOfDay,
  startOfMonth,
  differenceInDays,
  differenceInHours,
} from 'date-fns';

export const dateRangeUnits = {
  HOUR: 'hour',
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
};

// Determine appropriate units based on date range duration
export function getUnitsForDateRange(startDate, endDate) {
  if (!startDate || !endDate) {
    return [dateRangeUnits.DAY];
  }

  const days = differenceInDays(endDate, startDate);
  const hours = differenceInHours(endDate, startDate);

  // For ranges <= 2 days, show hourly option
  if (days <= 2) {
    return [dateRangeUnits.HOUR, dateRangeUnits.DAY];
  }

  // For ranges <= 14 days, show hourly and daily
  if (days <= 14) {
    return [dateRangeUnits.HOUR, dateRangeUnits.DAY];
  }

  // For ranges <= 60 days, show daily and weekly
  if (days <= 60) {
    return [dateRangeUnits.DAY, dateRangeUnits.WEEK];
  }

  // For ranges <= 365 days, show daily, weekly, and monthly
  if (days <= 365) {
    return [dateRangeUnits.DAY, dateRangeUnits.WEEK, dateRangeUnits.MONTH];
  }

  // For ranges > 365 days, show weekly, monthly, and yearly
  return [dateRangeUnits.WEEK, dateRangeUnits.MONTH, dateRangeUnits.YEAR];
}

// Get default unit for a date range
export function getDefaultUnitForDateRange(startDate, endDate) {
  if (!startDate || !endDate) {
    return dateRangeUnits.DAY;
  }

  const days = differenceInDays(endDate, startDate);

  if (days <= 2) return dateRangeUnits.HOUR;
  if (days <= 60) return dateRangeUnits.DAY;
  if (days <= 365) return dateRangeUnits.WEEK;
  return dateRangeUnits.MONTH;
}

export const dateRanges = {
  today: {
    label: 'Today',
    start: startOfDay(new Date()),
    end: endOfDay(new Date()),
    units: [dateRangeUnits.HOUR],
    defaultUnit: dateRangeUnits.HOUR,
  },
  yesterday: {
    label: 'Yesterday',
    start: startOfDay(subDays(new Date(), 1)),
    end: endOfDay(subDays(new Date(), 1)),
    units: [dateRangeUnits.HOUR],
    defaultUnit: dateRangeUnits.HOUR,
  },

  lastSevenDays: {
    label: 'Last 7 days',
    start: startOfDay(subDays(new Date(), 6)),
    end: endOfDay(new Date()),
    units: [dateRangeUnits.DAY, dateRangeUnits.HOUR],
    defaultUnit: dateRangeUnits.DAY,
  },
  lastFourWeeks: {
    label: 'Last 4 weeks',
    start: startOfDay(subDays(new Date(), 27)),
    end: endOfDay(new Date()),
    units: [dateRangeUnits.DAY, dateRangeUnits.WEEK],
    defaultUnit: dateRangeUnits.DAY,
  },
  lastThreeMonths: {
    label: 'Last 3 months',
    start: startOfDay(subMonths(new Date(), 3)),
    end: endOfDay(new Date()),
    units: [dateRangeUnits.DAY, dateRangeUnits.WEEK, dateRangeUnits.MONTH],
    defaultUnit: dateRangeUnits.WEEK,
  },
  lastTwelveMonths: {
    label: 'Last 12 months',
    start: startOfDay(subMonths(new Date(), 12)),
    end: endOfDay(new Date()),
    units: [dateRangeUnits.DAY, dateRangeUnits.WEEK, dateRangeUnits.MONTH],
    defaultUnit: dateRangeUnits.MONTH,
  },

  monthToDate: {
    label: 'Month to date',
    start: startOfMonth(new Date()),
    end: endOfDay(new Date()),
    units: [dateRangeUnits.DAY],
    defaultUnit: dateRangeUnits.DAY,
  },
  quarterToDate: {
    label: 'Quarter to date',
    start: startOfQuarter(new Date()),
    end: endOfDay(new Date()),
    units: [dateRangeUnits.DAY, dateRangeUnits.WEEK, dateRangeUnits.MONTH],
    defaultUnit: dateRangeUnits.WEEK,
  },
  yearToDate: {
    label: 'Year to date',
    start: startOfYear(new Date()),
    end: endOfDay(new Date()),
    units: [dateRangeUnits.DAY, dateRangeUnits.WEEK, dateRangeUnits.MONTH],
    defaultUnit: dateRangeUnits.MONTH,
  },

  allTime: {
    label: 'All time',
    start: startOfDay(subYears(new Date(), 10)),
    end: endOfDay(new Date()),
    units: [dateRangeUnits.DAY, dateRangeUnits.WEEK, dateRangeUnits.MONTH, dateRangeUnits.YEAR],
    defaultUnit: dateRangeUnits.MONTH,
  },

  // currentBillingPeriod is dynamically set in Dashboard.js based on seller's subscription
  currentBillingPeriod: {
    label: 'Current billing period',
    // Placeholder values that will be overridden
    start: startOfDay(new Date()),
    end: endOfDay(new Date()),
    units: [dateRangeUnits.DAY, dateRangeUnits.WEEK],
    defaultUnit: dateRangeUnits.DAY,
  },
};
